/**
 * 算力流水页面：展示充值、消费、退费等账单流水。
 */

import type { ReactElement } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, Zap } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useBillingRecordsPaginated } from '@/hooks/useBillingRecords'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores'
import type {
  BillingRecord,
  BillingRecordListParams,
  BillingRecordType,
} from '@/types/billing'

/** 账单筛选类型 */
type BillingFilter =
  | 'all'
  | 'recharge'
  | 'shared'
  | 'exclusive'
  | 'adjust'

/** 筛选下拉选项 */
const FILTER_OPTIONS: readonly { value: BillingFilter; label: string }[] = [
  { value: 'all', label: '全部' },
  { value: 'recharge', label: '充值' },
  { value: 'shared', label: '共享池' },
  { value: 'exclusive', label: '专享扣费' },
  { value: 'adjust', label: '后台调整' },
] as const

const SHARED_TYPES: readonly BillingRecordType[] = [
  'shared_consume',
] as const

/**
 * 根据筛选值获取后端 types 参数。
 * @param filter 筛选值
 * @returns types 列表（为空表示不传 types）
 */
function getTypesByFilter(filter: BillingFilter): BillingRecordType[] {
  switch (filter) {
    case 'recharge':
      return ['recharge', 'signup_bonus']
    case 'shared':
      return [...SHARED_TYPES]
    case 'exclusive':
      return ['exclusive_consume']
    case 'adjust':
      return ['admin_adjust']
    case 'all':
    default:
      return []
  }
}

/**
 * 格式化日期时间
 * @param dateString ISO 日期字符串
 * @returns 格式化后的日期时间字符串
 */
function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  const year = String(date.getFullYear())
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}`
}

/**
 * 获取流水类型展示文案
 * @param type 流水类型
 * @returns 文案
 */
function getRecordTypeLabel(type: BillingRecordType): string {
  switch (type) {
    case 'recharge':
      return '充值'
    case 'signup_bonus':
      return '注册赠送'
    case 'admin_adjust':
      return '后台调整'
    case 'shared_consume':
      return '共享池消费'
    case 'exclusive_consume':
      return '专享扣费'
    case 'client_seat':
      return '客户端席位'
    case 'client_plus':
      return 'Plus 席位'
    default:
      return type
  }
}

/**
 * 获取流水类型徽标样式
 * @param type 流水类型
 * @returns className
 */
function getRecordTypeBadgeClass(type: BillingRecordType): string {
  switch (type) {
    case 'recharge':
      return 'bg-emerald-500/10 text-emerald-700'
    case 'signup_bonus':
      return 'bg-sky-500/10 text-sky-700'
    case 'admin_adjust':
      return 'bg-slate-500/10 text-slate-700'
    case 'exclusive_consume':
      return 'bg-purple-500/10 text-purple-700'
    case 'shared_consume':
      return 'bg-amber-500/10 text-amber-700'
    case 'client_seat':
      return 'bg-indigo-500/10 text-indigo-700'
    case 'client_plus':
      return 'bg-amber-500/10 text-amber-700'
    default:
      return 'bg-slate-500/10 text-slate-700'
  }
}

/**
 * 格式化带符号的数值
 * @param value 数值
 * @returns 带正负号字符串
 */
function formatSignedNumber(value: number): string {
  if (value > 0) {
    return `+${String(value)}`
  }
  return String(value)
}

/**
 * 获取带符号数值的颜色样式
 * @param value 数值
 * @returns className
 */
function getSignedNumberClassName(value: number): string {
  if (value > 0) {
    return 'text-emerald-600'
  }
  if (value < 0) {
    return 'text-rose-600'
  }
  return 'text-muted-foreground'
}

/**
 * 从 metadata 中读取指定 key（仅 string 类型）
 * @param metadata metadata 对象
 * @param key 字段名
 * @returns 字符串值或 null
 */
function getMetadataString(metadata: Record<string, unknown>, key: string): string | null {
  const value: unknown = metadata[key]
  return typeof value === 'string' ? value : null
}

/**
 * 从 metadata 中读取指定 key（仅 number 类型）
 * @param metadata metadata 对象
 * @param key 字段名
 * @returns 数值或 null
 */
function getMetadataNumber(metadata: Record<string, unknown>, key: string): number | null {
  const value: unknown = metadata[key]
  return typeof value === 'number' ? value : null
}

/**
 * 获取记录补充说明
 * @param record 账单记录
 * @returns 补充说明（可空）
 */
function getRecordHint(record: BillingRecord): string | null {
  const metadata = record.metadata
  const orderNo = getMetadataString(metadata, 'order_no')
  if (orderNo !== null) {
    return '订单号：' + orderNo
  }

  if (record.type === 'signup_bonus') {
    const registrationSource = getMetadataString(metadata, 'registration_source')
    if (registrationSource === 'password') {
      return '新用户首次密码登录注册奖励'
    }
    if (registrationSource === 'sms') {
      return '新用户首次验证码登录注册奖励'
    }
    return '新用户首次注册奖励'
  }

  const reason = getMetadataString(metadata, 'reason')
  const cancelledBy = getMetadataString(metadata, 'cancelled_by')
  const cancelledPending = getMetadataNumber(metadata, 'cancelled_pending')
  const cancelledPaused = getMetadataNumber(metadata, 'cancelled_paused')
  const autoCancelled = metadata['auto_cancelled']
  if (cancelledPending !== null || cancelledPaused !== null) {
    return (
      '取消退费（pending: ' +
      String(cancelledPending ?? 0) +
      '，paused: ' +
      String(cancelledPaused ?? 0) +
      '）'
    )
  }
  if (reason === 'task_failed') {
    return '任务最终失败退费'
  }
  if (reason === 'task_cancelled') {
    const autoTag = autoCancelled === true ? '（自动生效）' : ''
    const byText =
      cancelledBy !== null && cancelledBy.length > 0 ? '，发起方：' + cancelledBy : ''
    return '运行中任务取消退费' + autoTag + byText
  }
  if (reason === 'task_completed') {
    return '任务成功扣费'
  }
  if (reason !== null && reason.length > 0) {
    return '备注：' + reason
  }

  const serverID = getMetadataString(metadata, 'server_id')
  if (serverID !== null) {
    return '服务器：' + serverID
  }
  const totalCount = getMetadataNumber(metadata, 'total_count')
  if (totalCount !== null) {
    return '任务数量：' + String(totalCount)
  }
  return null
}

/**
 * 获取算力变动字段的展示名称
 * @param record 账单记录
 * @returns 文案
 */
function getPowerDeltaLabel(record: BillingRecord): string {
  switch (record.type) {
    case 'shared_consume':
    case 'exclusive_consume':
      return '扣除算力值'
    case 'recharge':
      return '充值算力'
    case 'signup_bonus':
      return '赠送算力'
    case 'admin_adjust':
      return record.balanceDelta >= 0 ? '赠送算力' : '扣减算力'
    default:
      return '算力变动'
  }
}

function getBalanceAfterLabel(record: BillingRecord): string {
  switch (record.type) {
    case 'shared_consume':
    case 'exclusive_consume':
      return '扣费后余额'
    default:
      return '余额'
  }
}

/**
 * 获取算力变动字段的展示值
 * @param record 账单记录
 * @returns 展示文本
 */
function getPowerDeltaValueText(record: BillingRecord): string {
  switch (record.type) {
    case 'shared_consume':
    case 'exclusive_consume':
    case 'recharge':
    case 'signup_bonus':
    case 'admin_adjust':
      return String(Math.abs(record.balanceDelta))
    default:
      return formatSignedNumber(record.balanceDelta)
  }
}

/**
 * 获取消费记录的进度备注（成功数/总数）
 * @param record 账单记录
 * @returns 备注文本或 null
 */
function getConsumeProgressHint(record: BillingRecord): string | null {
  if (record.type !== 'shared_consume') {
    return null
  }

  const totalCount = record.batchTaskTotalCount
  const successCount = record.batchTaskSuccessCount
  if (totalCount === undefined || successCount === undefined) {
    return null
  }
  return '成功 ' + String(successCount) + '/' + String(totalCount)
}

/**
 * 获取记录备注
 * @param record 账单记录
 * @returns 备注文本或 null
 */
function getRecordRemark(record: BillingRecord): string | null {
  const parts: string[] = []

  const consumeHint = getConsumeProgressHint(record)
  if (consumeHint !== null) {
    parts.push(consumeHint)
  }

  const hint = getRecordHint(record)
  if (hint !== null) {
    parts.push(hint)
  }

  return parts.length > 0 ? parts.join('，') : null
}

/**
 * 获取关联任务备注
 * @param record 账单记录
 * @returns 任务备注（可空）
 */
function getTaskRemark(record: BillingRecord): string | null {
  if (record.refType !== 'batch_task') {
    return null
  }

  const apiRemark = record.submitTaskRemark?.trim()
  if (apiRemark !== undefined && apiRemark.length > 0) {
    return apiRemark
  }

  const metadataRemark = getMetadataString(record.metadata, 'remark')
  if (metadataRemark !== null && metadataRemark.trim().length > 0) {
    return metadataRemark.trim()
  }

  return null
}

interface BillingRecordTableRowProps {
  record: BillingRecord
}

/**
 * 账单表格行
 * @param props 组件属性
 * @returns ReactElement
 */
function BillingRecordTableRow({ record }: BillingRecordTableRowProps): ReactElement {
  const typeLabel =
    record.type === 'admin_adjust'
      ? record.balanceDelta >= 0
        ? '后台赠送'
        : '后台扣减'
      : getRecordTypeLabel(record.type)

  const taskRemark = getTaskRemark(record)
  const remarkText = getRecordRemark(record)

  return (
    <tr className="border-b align-top last:border-b-0">
      <td className="whitespace-nowrap px-4 py-3 text-sm">{formatDateTime(record.createdAt)}</td>

      <td className="whitespace-nowrap px-4 py-3">
        <span
          className={cn(
            'inline-flex rounded-full px-2 py-1 text-xs font-medium',
            getRecordTypeBadgeClass(record.type)
          )}
        >
          {typeLabel}
        </span>
      </td>

      <td className="px-4 py-3 text-sm">
        {taskRemark !== null ? (
          <p className="max-w-[280px] break-all text-foreground/90" title={taskRemark}>
            {taskRemark}
          </p>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </td>

      <td className="whitespace-nowrap px-4 py-3">
        <p className="text-xs text-muted-foreground">{getPowerDeltaLabel(record)}</p>
        <p className={cn('font-semibold', getSignedNumberClassName(record.balanceDelta))}>
          {getPowerDeltaValueText(record)}
        </p>
      </td>

      <td className="whitespace-nowrap px-4 py-3">
        <p className="text-xs text-muted-foreground">{getBalanceAfterLabel(record)}</p>
        <p className="font-semibold">{record.balanceAfter ?? '-'}</p>
      </td>

      <td className="px-4 py-3 text-sm">
        {remarkText !== null ? (
          <p className="max-w-[320px] break-all text-muted-foreground" title={remarkText}>
            {remarkText}
          </p>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </td>
    </tr>
  )
}

/**
 * 算力流水页面
 * @returns 页面 ReactElement
 */
export function BillingRecordsPage(): ReactElement {
  const user = useAuthStore((state) => state.user)
  const [filter, setFilter] = useState<BillingFilter>('all')
  const [page, setPage] = useState<number>(1)

  const types = useMemo((): BillingRecordType[] => getTypesByFilter(filter), [filter])
  const pageSize = 20

  const queryParams = useMemo((): BillingRecordListParams => {
    const params: BillingRecordListParams = {
      current: page,
      pageSize,
    }
    if (types.length > 0) {
      params.types = types
    }
    return params
  }, [page, pageSize, types])

  const enabled = user !== null
  const { data, isLoading, isError, error, refetch } = useBillingRecordsPaginated(
    queryParams,
    enabled
  )

  const records = data?.records ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  /**
   * 切换筛选条件
   * @param nextFilter 新筛选值
   */
  const handleFilterChange = useCallback((nextFilter: BillingFilter): void => {
    setFilter(nextFilter)
    setPage(1)
  }, [])

  /**
   * 上一页
   */
  const handlePrevPage = useCallback((): void => {
    setPage((prev) => Math.max(1, prev - 1))
  }, [])

  /**
   * 下一页
   */
  const handleNextPage = useCallback((): void => {
    setPage((prev) => Math.min(totalPages, prev + 1))
  }, [totalPages])

  if (user === null) {
    return (
      <div className="space-y-6">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">算力流水</h1>
          <p className="text-muted-foreground">请先登录后查看算力流水。</p>
        </div>
        <Button asChild>
          <Link to="/login">去登录</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">算力流水</h1>
          <p className="text-muted-foreground">
            展示充值、共享池消费/退费、专享扣费等记录。
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="w-full sm:w-[180px]">
            <Select value={filter} onValueChange={handleFilterChange}>
              <SelectTrigger>
                <SelectValue placeholder="选择类型" />
              </SelectTrigger>
              <SelectContent>
                {FILTER_OPTIONS.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button asChild variant="secondary">
            <Link to="/pricing">
              <Zap className="mr-2 h-4 w-4" />
              去充值
            </Link>
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-2xl border bg-card p-6">
          <div className="h-5 w-40 animate-pulse rounded bg-muted" />
          <div className="mt-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded bg-muted" />
            ))}
          </div>
        </div>
      ) : null}

      {isError ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border bg-card py-16 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <h3 className="mt-4 text-lg font-semibold">加载失败</h3>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">
            {error instanceof Error ? error.message : '加载失败，请重试'}
          </p>
          <Button onClick={(): void => void refetch()} className="mt-6" variant="outline">
            重新加载
          </Button>
        </div>
      ) : null}

      {!isLoading && !isError ? (
        <div className="rounded-2xl border bg-card">
          <div className="flex flex-col gap-2 border-b p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              共 {total} 条记录，当前第 {page} / {totalPages} 页
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={handlePrevPage}
              >
                上一页
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={handleNextPage}
              >
                下一页
              </Button>
            </div>
          </div>

          {records.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <Zap className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="mt-4 text-lg font-semibold">暂无流水记录</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                产生充值或消费后会在这里显示。
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[960px] border-collapse">
                <thead className="bg-muted/40">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="whitespace-nowrap px-4 py-3 font-medium">时间</th>
                    <th className="whitespace-nowrap px-4 py-3 font-medium">类型</th>
                    <th className="px-4 py-3 font-medium">关联任务备注</th>
                    <th className="whitespace-nowrap px-4 py-3 font-medium">算力变动</th>
                    <th className="whitespace-nowrap px-4 py-3 font-medium">余额</th>
                    <th className="px-4 py-3 font-medium">备注</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record) => (
                    <BillingRecordTableRow key={record.id} record={record} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}
